<template>
  <footer class="bg-slate-950 text-slate-300 border-t border-white/5 font-sans">
    <!-- Main Footer -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div class="grid lg:grid-cols-4 md:grid-cols-2 gap-12 text-left">
        <!-- Company Info -->
        <div class="lg:col-span-1 text-left">
          <div class="flex items-center mb-8 group">
            <div class="w-10 h-10 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-xl flex items-center justify-center mr-3 shadow-lg group-hover:rotate-12 transition-transform">
              <span class="text-slate-950 font-black text-xl">
                 <a href="/">
            <img alt="Logo" class="h-10 w-auto" :src="logo" />
                 </a> 
              </span>
            </div>
            <div class="text-left">
              <h3 class="text-xl font-black text-white tracking-tighter">prince<span class="text-emerald-400">Tech</span></h3>
              <p class="text-xs text-slate-500 font-bold uppercase tracking-widest text-left">Innovation House</p>
            </div>
          </div>

          <p class="text-slate-400 mb-8 leading-relaxed text-sm text-left">
            {{ companyInfo.description }}
          </p>

          <div class="space-y-4">
            <div class="flex items-center text-sm">
              <MapPin class="w-4 h-4 mr-3 text-emerald-500 flex-shrink-0" />
              <span>{{ companyInfo.address }}</span>
            </div>
            <div class="flex items-center text-sm">
              <Phone class="w-4 h-4 mr-3 text-blue-500 flex-shrink-0" />
              <a
                :href="`tel:${companyInfo.phone.replace(/[^+\d]/g, '')}`"
                class="hover:text-emerald-400 transition-colors"
              >
                {{ companyInfo.phone }}
              </a>
            </div>
            <div class="flex items-center text-sm">
              <Mail class="w-4 h-4 mr-3 text-indigo-500 flex-shrink-0" />
              <a
                :href="`mailto:${companyInfo.email}`"
                class="hover:text-emerald-400 transition-colors"
              >
                {{ companyInfo.email }}
              </a>
            </div>
          </div>
        </div>

        <!-- Project Engineering -->
        <div class="text-left">
          <h4 class="text-sm font-black mb-8 text-white uppercase tracking-[0.2em] text-left">Engineering</h4>
          <ul class="space-y-4 text-left">
            <li v-for="service in serviceLinks" :key="service.name">
              <router-link
                to="/Services"
                class="text-slate-400 hover:text-emerald-400 transition-all duration-200 flex items-center text-sm group"
              >
                <component :is="service.icon" class="w-4 h-4 mr-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                {{ service.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Company Links -->
        <div class="text-left">
          <h4 class="text-sm font-black mb-8 text-white uppercase tracking-[0.2em] text-left">Company</h4>
          <ul class="space-y-4 text-left">
            <li v-for="link in quickLinks" :key="link.name">
              <router-link
                :to="link.path"
                class="text-slate-400 hover:text-blue-400 transition-all duration-200 flex items-center text-sm group"
              >
                <component :is="link.icon" class="w-4 h-4 mr-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                {{ link.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Newsletter & Social -->
        <div class="text-left">
          <h4 class="text-sm font-black mb-8 text-white uppercase tracking-[0.2em] text-left">Newsletter</h4>
          <p class="text-xs text-slate-500 mb-6 font-medium text-left">Join 2,000+ others receiving our tech insights monthly.</p>
          
          <!-- <div class="flex mb-8">
            <input
              type="email"
              placeholder="engineer@company.com"
              class="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-l-xl text-white text-sm focus:outline-none focus:border-emerald-500/50 transition-colors"
            />
            <button
              class="bg-emerald-500 px-5 py-3 rounded-r-xl hover:bg-emerald-400 transition-colors group"
            >
              <Send class="w-4 h-4 text-slate-950 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div> -->

          <div class="flex space-x-3">
            <a
              v-for="social in socialLinks"
              :key="social.name"
              :href="social.url"
              target="_blank"
              rel="noopener noreferrer"
              class="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all duration-300"
            >
              <component :is="social.icon" class="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Footer border -->
    <div class="border-t border-white/5 bg-slate-950/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="flex flex-col md:flex-row justify-between items-center gap-6">
          <div class="flex flex-col md:flex-row items-center gap-6">
            <p class="text-xs text-slate-500 font-medium whitespace-nowrap">
              © {{ currentYear }} princeTech Company. Built with Precision.
            </p>
            
          </div>

          <div class="flex items-center text-xs font-bold text-slate-500 italic whitespace-nowrap">
            <Terminal class="w-4 h-4 mr-2 text-emerald-500" />
            Where Vision Meets Innovation
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import logo from "@/assets/logo.jpeg";
import {
  MapPin,
  Phone,
  Mail,
  // Send,
  Home,
  User,
  Briefcase,
  MessageCircle,
  Terminal,
  Code,
  Smartphone,
  Cpu,
  Layers,
  Shield,
  Zap,
  Linkedin,
  Github,
  Facebook
} from "lucide-vue-next";
import { computed } from "vue";

const currentYear = computed(() => new Date().getFullYear());

const companyInfo = {
  description:
    "A premier technology agency specializing in custom software architecture, web ecosystems, and cross-platform mobile experiences.",
  address: "Kampala,  kyanja Ring Road, Next to Amreena Mall",
  phone: "+256700648795",
  email: "princetechcompany@gmail.com",
};

const quickLinks = [
  { name: "Home", path: "/", icon: Home },
  { name: "About", path: "/About", icon: User },
  { name: "Services", path: "/Services", icon: Briefcase },
  { name: "Portfolio", path: "/PortFolio", icon: Zap },
  { name: "Contact", path: "/ContactUs", icon: MessageCircle },
];

const serviceLinks = [
  { name: "Web Systems", icon: Code },
  { name: "Mobile Apps", icon: Smartphone },
  { name: "Cloud Infra", icon: Cpu },
  { name: "UI/UX Design", icon: Layers },
  { name: "Cybersecurity", icon: Shield },
];

const socialLinks = [
  { name: "LinkedIn", icon: Linkedin, url: "https://www.linkedin.com/in/princetech-company-180479400" },
  { name: "Facebook", icon: Facebook, url: "https://www.facebook.com/profile.php?id=100094199523884" },
  { name: "GitHub", icon: Github, url: "#" },
  
];
</script>
